/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import styled from 'styled-components';
import { Box, sx, themeGet } from '../..';
import { UnstyledTextInput } from '../TextInput/UnstyledTextInput';
const widthMap = {
  xsmall: 80,
  small: 160,
  medium: 240,
  large: 320,
  xlarge: 480
};
const getMaxWidth = width => {
  return !width ? `100%` : width in widthMap ? widthMap[width] : +width;
};
export const borderRadius = () => 3;
export const gridSize = () => 8;
export const fontSize = () => 14;
export const fontSizeSmall = () => 11;
export const fontFamily = () => `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`;
export const codeFontFamily = () => `'SFMono-Medium', 'SF Mono', 'Segoe UI Mono', 'Roboto Mono', 'Ubuntu Mono', Menlo, Consolas, Courier, monospace`;
export const componentTokens = {
  disabledBackgroundColor: themeGet('colors.background.disabled'),
  defaultBackgroundColor: themeGet('colors.background.input'),
  defaultBackgroundColorFocus: themeGet('colors.background.input.pressed'),
  defaultBackgroundColorHover: themeGet('colors.background.input.hovered'),
  defaultBorderColor: themeGet('colors.border.input'),
  defaultBorderColorFocus: themeGet('colors.border.focused'),
  subtleBorderColorHover: themeGet('colors.border.input'),
  transparent: 'transparent',
  textColor: themeGet('colors.text.default'),
  disabledTextColor: themeGet('colors.text.disabled'),
  placeholderTextColor: themeGet('colors.text.subtlest')
};
const disabledRules = {
  backgroundColor: componentTokens.disabledBackgroundColor,
  backgroundColorHover: componentTokens.disabledBackgroundColor,
  borderColor: componentTokens.disabledBackgroundColor,
  textColor: componentTokens.disabledTextColor
};
const invalidRules = {
  // ----
  backgroundColor: componentTokens.defaultBackgroundColor,
  backgroundColorHover: componentTokens.defaultBackgroundColorHover,
  // ^--- the above values aren't used directly they remain because its exposed by the `textFieldColors` export
  backgroundColorFocus: componentTokens.defaultBackgroundColorFocus,
  borderColor: themeGet('color.border.danger'),
  borderColorFocus: componentTokens.defaultBorderColorFocus
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const backgroundColor = {
  standard: componentTokens.defaultBackgroundColor,
  subtle: componentTokens.transparent,
  none: componentTokens.transparent
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const backgroundColorFocus = {
  standard: componentTokens.defaultBackgroundColorFocus,
  subtle: componentTokens.defaultBackgroundColorFocus,
  none: componentTokens.transparent
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const backgroundColorHover = {
  standard: componentTokens.defaultBackgroundColorHover,
  subtle: componentTokens.defaultBackgroundColorHover,
  none: componentTokens.transparent
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const borderColor = {
  standard: componentTokens.defaultBorderColor,
  subtle: componentTokens.transparent,
  none: componentTokens.transparent
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const borderColorFocus = {
  standard: componentTokens.defaultBorderColorFocus,
  subtle: componentTokens.defaultBorderColorFocus,
  none: componentTokens.transparent
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const borderColorHover = {
  standard: componentTokens.defaultBorderColor,
  subtle: componentTokens.subtleBorderColorHover,
  none: componentTokens.transparent
};
export const AtlasTextfieldWrapper = styled(Box).withConfig({
  displayName: "AtlasTextfieldWrapper",
  componentId: "sc-1mom8yf-0"
})(["align-items:center;border-radius:", ";border-width:2;border-style:", ";box-sizing:border-box;display:flex;flex:1 1 100%;font-size:", ";justify-content:space-between;max-width:", ";overflow:hidden;transition:background-color 0.2s ease-in-out,border-color 0.2s ease-in-out;word-wrap:break-word;vertical-align:top;pointer-events:auto;cursor:text;background-color:", ";border-color:", ";color:", ";&:hover:not([data-disabled]){background-color:", ";border-color:", ";}&:hover:not([data-disabled]){background-color:", ";border-color:", ";}&:focus-within:not([data-disabled]){background-color:", ";border-color:", ";}&[data-disabled]{color:", ";cursor:not-allowed;background-color:", ";border-color:", ";}&[data-invalid],&[data-invalid]:hover{border-color:", ";}&[data-invalid]:focus-within{background-color:", ";border-color:", ";}@media screen and (-ms-high-contrast:active){&[data-invalid]:focus-within{border-color:'Highlight';}&:focus-within{border-color:'Highlight';}&[data-disabled]{border-color:'GrayText';}}", ""], themeGet('radii.1'), p => p.appearance === 'none' ? 'none' : 'solid', themeGet('fontSizes.2'), p => p.width !== '100%' ? getMaxWidth(p.width) + 'px' : p.width, p => p.appearance ? backgroundColor[p.appearance] : '', p => p.appearance ? borderColor[p.appearance] : '', p => componentTokens.textColor, p => p.appearance ? backgroundColorHover[p.appearance] : '', p => p.appearance ? borderColorHover[p.appearance] : '', p => p.appearance ? backgroundColorHover[p.appearance] : '', p => p.appearance ? borderColorHover[p.appearance] : '', p => p.appearance ? backgroundColorFocus[p.appearance] : '', p => p.appearance ? borderColorFocus[p.appearance] : '', p => p.appearance ? disabledRules.textColor : '', p => p.appearance === 'standard' ? disabledRules.backgroundColor : '', p => p.appearance === 'standard' ? disabledRules.borderColor : '', p => p.appearance ? invalidRules.borderColor : '', p => p.appearance ? invalidRules.backgroundColorFocus : '', p => p.appearance ? invalidRules.borderColorFocus : '', sx);
export const StyledAtlasTextInput = styled(UnstyledTextInput).withConfig({
  displayName: "AtlasTextfieldWrapper__StyledAtlasTextInput",
  componentId: "sc-1mom8yf-1"
})(["background-color:transparent;border:0;box-sizing:border-box;color:inherit;cursor:inherit;font-size:", ";min-width:0;outline:none;width:100%;line-height:", ";font-family:", ";&[data-monospaced]{font-family:", ";}&[data-compact]{padding:4px 6px;height:", ";}&:not([data-compact]){padding:8px 6px;height:", "em;}&[disabled]{-webkit-text-fill-color:", ";}&::-ms-clear{display:none;}&:invalid{box-shadow:none;}&::placeholder{color:", ";&:disabled{color:", ";}}@media screen and (-ms-high-contrast:active){&[disabled]{color:'GrayText';}}"], themeGet('fontSizes.2'), 8 * 2.5 / fontSize(), fontFamily(), codeFontFamily(), (8 * 3.5 / 14).toFixed(2) + `em`, (8 * 4.5 / 14).toFixed(2), disabledRules.textColor, componentTokens.placeholderTextColor, disabledRules.textColor);